import { trackEvent } from '@dispatch/web/telemetry';

/**
 * Toolbar-driven expand / collapse-all for the Claim Owner capacity band, and
 * the usage counter for both ways a band gets opened (ADO #104914).
 *
 * WHY BOTH LIVE IN ONE MODULE. A dispatcher can open a strip two ways — the
 * per-row expander control, or the toolbar's bulk Expand — and those are
 * different signals that must not be double counted. The bulk runner and the
 * row-expand handler share one in-flight marker below, so a 40-resource bulk
 * emits ONE event rather than 40. Splitting them would put that marker across a
 * module boundary and the double count is exactly what would come back.
 *
 * NO NEW REFRESH PATH. Both controls go through Bryntum's own
 * `RowExpander#expand` / `#collapse`, so SchedulerCore's in-place
 * `refreshCapacityBands` (wired to `rowExpand` / `timeAxisChange`) does the
 * rendering, unchanged. RowExpander caches its body element per record, so a
 * collapse/re-expand is NOT a refresh and must never be used as one.
 */

/** Single event name for both triggers; `trigger` separates them. */
export const CAPACITY_EXPAND_EVENT = 'CapacityBandExpanded';

/** Which gesture opened the strip. */
export type CapacityExpandTrigger = 'row-control' | 'toolbar-bulk';

/** Trigger labels, named so a typo cannot silently split the counter in two. */
const ROW_CONTROL_TRIGGER: CapacityExpandTrigger = 'row-control';
const TOOLBAR_BULK_TRIGGER: CapacityExpandTrigger = 'toolbar-bulk';

/**
 * What a bulk expand acted on: the resources selected at click time, or —
 * with nothing selected — every resource currently listed.
 */
export type CapacityExpandScope = 'selection' | 'listed';

/**
 * Minimal record shape. Only the id is read: everything else on a resource
 * record (name, description, team, county, the loss location on its events) is
 * exactly what must never reach telemetry, so it is not modelled here at all.
 */
export interface CapacityExpanderRecord {
  id: string | number;
}

export interface CapacityRowExpanderFeature {
  /**
   * `true` whenever the tenant flag or the user's capacity toggle is off —
   * SchedulerCore flips this on the live instance. An expand is a no-op then,
   * and Bryntum's own `doDisable` has already cleared the expanded records.
   */
  disabled?: boolean;
  /**
   * Bryntum's per-record expanded-state map — the ONLY place the set of
   * currently expanded rows exists. RowExpander 7.2.0 exposes no public
   * `expandedRecords` / `isExpanded` / `collapseAll`, so collapse-all has to
   * read this. Optional here so a version that renames it degrades to "nothing
   * to collapse" instead of throwing.
   */
  recordStateMap?: Map<CapacityExpanderRecord, unknown>;
  expand(record: CapacityExpanderRecord): unknown;
  collapse(record: CapacityExpanderRecord): unknown;
}

/** The slice of the live Bryntum instance these controls touch. */
export interface CapacityExpansionScheduler {
  features?: { rowExpander?: CapacityRowExpanderFeature };
  /** Filtered, currently visible resource rows — "every resource listed". */
  resourceStore?: { records?: readonly CapacityExpanderRecord[] };
  /** Row selection (`selectionMode.row` + `multiSelect`), so resource records. */
  selectedRecords?: readonly CapacityExpanderRecord[];
}

export interface CapacityBulkExpandResult {
  scope: CapacityExpandScope;
  /** Records handed to `expand()`. */
  requestedCount: number;
  /** Records Bryntum actually accepted — already-open rows are excluded. */
  expandedCount: number;
  selectedCount: number;
  listedCount: number;
}

/**
 * True for the synchronous duration of a bulk expand, which is how the per-row
 * counter knows to stay quiet.
 *
 * A plain flag works because `RowExpander#expand` fires `beforeRowExpand` on
 * the grid SYNCHRONOUSLY — the `await client.trigger(...)` expression is
 * evaluated before the async function suspends — so every suppressed emission
 * happens inside the loop below, not after it.
 *
 * It is deliberately ONLY a marker. An earlier version had it carry the bulk's
 * accepted count too, which made `expandedCount` silently read 0 whenever the
 * listener happened not to be attached: a number that quietly depends on a
 * subscription elsewhere is the "passes while asserting nothing" failure mode.
 * The count is derived from `recordStateMap` instead — the same single source
 * of truth collapse-all reads.
 */
let bulkDepth = 0;

/**
 * Handle one `beforeRowExpand`.
 *
 * Returns `void` — never `false` — because `beforeRowExpand` is a VETO event:
 * `expand()` bails on `=== false`, so a counter that accidentally returned
 * false would silently stop every band from opening.
 */
export function onCapacityRowExpand(resourceId: string): void {
  if (bulkDepth > 0) return;

  trackEvent(
    CAPACITY_EXPAND_EVENT,
    { trigger: ROW_CONTROL_TRIGGER, resourceId },
    { expandedCount: 1 }
  );
}

/**
 * Open a strip for every selected resource, or — with nothing selected — for
 * every resource currently listed.
 *
 * EXPANSION DOES NOT FOLLOW SELECTION. The selection is read once, here, at
 * click time. The board replaces the selection on a plain click, so a
 * dispatcher working the board changes it constantly; if expansion tracked it,
 * every click would slam strips shut. After this call each row holds its own
 * state until the dispatcher collapses it — nothing in this module ever
 * collapses a row because it left the selection.
 *
 * ONE EVENT, NOT ONE PER RESOURCE. A 40-resource expand is ONE dispatcher
 * action, and 40 events would (a) flood the counter that Task 12 exists to
 * read, (b) inflate "how many times was the feature used" by the size of the
 * board rather than by usage, and (c) be the first thing App Insights sampling
 * throws away. The count is carried as a measurement instead, which answers
 * "how many strips did they open at once" — a question per-resource events
 * cannot answer without re-aggregating them.
 */
export function expandCapacityBands(
  scheduler: CapacityExpansionScheduler | null | undefined
): CapacityBulkExpandResult | null {
  const rowExpander = scheduler?.features?.rowExpander;
  // Disabled means flag-off or toggle-off. The controls are not rendered then,
  // so this is belt-and-braces: `expand()` would no-op anyway.
  if (!rowExpander || rowExpander.disabled) return null;

  const selected = scheduler?.selectedRecords ?? [];
  const listed = scheduler?.resourceStore?.records ?? [];
  const scope: CapacityExpandScope =
    selected.length > 0 ? 'selection' : 'listed';
  const targets = scope === 'selection' ? selected : listed;

  // Counted BEFORE the loop: `expand()` mutates `recordStateMap`, so after it
  // every target looks as though it had always been open. An absent map (a
  // Bryntum rename) reports 0 rather than guessing `targets.length` — the same
  // honest degradation collapse-all makes.
  const alreadyOpen = rowExpander.recordStateMap;
  const expandedCount = alreadyOpen
    ? targets.filter((record) => !alreadyOpen.has(record)).length
    : 0;

  bulkDepth += 1;
  try {
    // Deliberately NOT awaited. `expand()`'s promise resolves only once the
    // row is rendered into view, which for a row scrolled out of view can be
    // much later or never — awaiting 40 of them would hang the handler.
    targets.forEach((record) => {
      rowExpander.expand(record);
    });
  } finally {
    // In a `finally` so a throwing `expand()` cannot mute the per-row counter
    // for the rest of the session.
    bulkDepth -= 1;
  }

  const result: CapacityBulkExpandResult = {
    scope,
    requestedCount: targets.length,
    expandedCount,
    selectedCount: selected.length,
    listedCount: listed.length,
  };

  // Emitted even when nothing opened (everything was already expanded, or the
  // board is empty): the click itself is the usage signal, and expandedCount 0
  // is how a no-op click stays distinguishable from a productive one.
  trackEvent(
    CAPACITY_EXPAND_EVENT,
    { trigger: TOOLBAR_BULK_TRIGGER, scope },
    {
      requestedCount: result.requestedCount,
      expandedCount: result.expandedCount,
      selectedCount: result.selectedCount,
      listedCount: result.listedCount,
    }
  );

  return result;
}

/**
 * Close every open strip in one action.
 *
 * This is the stated mitigation for the band's one real cost: each expanded
 * strip inserts rows between one resource's task row and the next, so opening
 * many resources lengthens the path a drag has to travel across the board.
 * Strips must never be the only way back to a dense board — which is why this
 * control is paired with Expand rather than left to per-row collapsing.
 *
 * NOT counted in telemetry: Task 12 measures whether the band gets OPENED.
 *
 * The expanded set is snapshotted first because `collapse()` deletes from the
 * very map being iterated. Collapsing only the open rows is also deliberate —
 * `collapse()` does not early-return for a closed row, so looping the whole
 * store would fire a spurious `rowCollapse` and a re-render per resource.
 */
export function collapseAllCapacityBands(
  scheduler: CapacityExpansionScheduler | null | undefined
): number {
  const rowExpander = scheduler?.features?.rowExpander;
  if (!rowExpander) return 0;

  const expanded = Array.from(rowExpander.recordStateMap?.keys() ?? []);
  expanded.forEach((record) => {
    rowExpander.collapse(record);
  });
  return expanded.length;
}
